// pages/FormViewerPage/services/formViewerService/paymentOptions.ts — opsi bayar + default simpel (split dari options.ts)
import type { Form, PaymentSettings } from '../../../../types';
import type { AddressData } from '../../../../components/AddressInput';
import { isNoCodZoneProvince } from '../../../../data/adZones';
import { isCodBlockedByShipping, resolveCodShippingLimit } from '../../../../constants/codRules';

/**
 * Mencari kombinasi varian default untuk Mode Simpel / Lead Form:
 * Mengambil varian dengan harga jual termurah. Jika ada beberapa dengan harga sama,
 * ambil yang letaknya paling bawah di daftar varian.
 */
export function getSimpleModeDefaultCombination(combinations: any[]): any | null {
    if (!Array.isArray(combinations) || combinations.length === 0) return null;
    let minPrice = Infinity;
    let chosen: any = null;
    for (let i = 0; i < combinations.length; i++) {
        const combo = combinations[i];
        const price = typeof combo.sellingPrice === 'number' ? combo.sellingPrice : (Number(combo.sellingPrice) || 0);
        if (price <= minPrice) {
            minPrice = price;
            chosen = combo;
        }
    }
    return chosen || combinations[combinations.length - 1];
}

export function computeSortedPaymentKeys(
    form: Form | null,
    addressData: AddressData,
    shippingCost: number = 0,
    // Rute-lemah (aturan user 2026-10-17): tak ada kurir ≥80% → COD dikunci
    // (risiko retur tinggi), ongkir tetap tampil + bisa dipilih non-COD.
    lowPerfNoCod: boolean = false,
    // Ongkir ASLI sebelum subsidi (2026-11-04): cek COD = MAX(final, asli).
    shippingOriginalCost?: number,
): Array<keyof PaymentSettings> {
    if (!form) return [];
    // COD tidak tersedia utk provinsi zona Z5/Z6 (Regular/Diblokir) — filter di SEMUA mode
    // alamat (bukan cuma flat_regular). Provinsi belum dipilih (mode textarea) → COD tetap
    // tampil, ditangani post-analysis oleh autoSwitchCodToTransfer di backend.
    const codBlocked = !!addressData.province && isNoCodZoneProvince(addressData.province);
    // MODE DROPDOWN+WIZARD (2026-09-29, koreksi 2026-11-04): COD mati kalau ongkir
    // ASLI > maks — pengganti logika lama berbasis pulau (COD Jawa / TF luar Jawa).
    const codLimit = resolveCodShippingLimit(form.paymentSettings.cod);
    const codBlockedByShipping = codLimit.enabled && isCodBlockedByShipping(shippingCost, form.addressInputMode, codLimit.max, shippingOriginalCost);
    const codVisible = !!form.paymentSettings.cod?.visible;
    return (Object.keys(form.paymentSettings) as Array<keyof PaymentSettings>).filter(key => {
        const s = form!.paymentSettings[key];
        if (!s?.visible) return false;
        // Grup Duitku: tampil kalau ada metode dipilih. Kesiapan kredensial
        // dicek di UI (DuitkuMethodsBlock sembunyi sendiri kalau belum diatur).
        if (key === 'duitku') {
            const methods = (s as any)?.methods;
            return Array.isArray(methods) && methods.length > 0;
        }
        // Kalau "Bayar di Tempat (COD)" aktif, varian COD split (Jawa-Bali / Luar Jawa-Bali)
        // tidak perlu ditampilkan — cukup 1 opsi COD. Split hanya dipakai form yang mematikannya.
        if ((key === 'codJawaBali' || key === 'codLuarJawaBali') && codVisible) return false;
        const isCodKey = key === 'cod' || key === 'codJawaBali' || key === 'codLuarJawaBali';
        if (isCodKey && codBlocked) return false;
        if (isCodKey && codBlockedByShipping) return false;
        if (isCodKey && lowPerfNoCod) return false;
        return true;
    }).sort((a, b) => ((form!.paymentSettings[a] as any)?.sort || 0) - ((form!.paymentSettings[b] as any)?.sort || 0));
}
